import { useAuth } from '@/auth/useAuth';
import { NuevaEvidenciaModal } from '@/components/asesorias/NuevaEvidenciaModal';
import { RevisionFilterSelect, type FiltroRevision } from '@/components/asesorias/RevisionFilterSelect';
import type { RechazosTernaCampos } from '@/components/MatrizEvaluacion';
import { asesoriasService } from '@/services/asesorias.service';
import {
  agruparHistorialPorElemento,
  estaRechazadoVigente,
  estaResuelto,
  tieneRechazoActivo,
  type RevisionEntry,
} from '@/utils/revisionHistorial';
import { buildGruposEvaluacionDesdeTernas, type GrupoEvaluacion, MatrizEvaluacion } from '@/components/MatrizEvaluacion';
import { QuickActionsSpeedDial } from '@/components/ui/QuickActionsSpeedDial';
import { Spinner } from '@/components/ui/Spinner';
import { useRequisitosEvaluacion } from '@/hooks';
import '@/pages/admin/admin.css';
import { dashboardService } from '@/services/dashboard.service';
import { filterGruposRevision } from '@/utils/filterEvaluacion';
import { type CambioTernaEvaluacion, requisitosService } from '@/services/requisitos.service';
import { evaluacionStore, useEvaluacionStore } from '@/stores/useEvaluacionStore';
import { type CambioTerna, evaluacionTernaStore, useEvaluacionTernaStore } from '@/stores/useEvaluacionTernaStore';
import { CambioRequisito } from '@/types/evaluacion';
import type { AsesoriaEstado } from '@/utils/asesoriaStatus';
import { isAsesoriaEditable } from '@/utils/asesoriaStatus';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { AlertCircle, FileUp, Filter } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

// Configuración del auto-guardado
const AUTO_SAVE_DELAY = 8000; // 8 segundos después del último cambio

/**
 * Calcula si la asesoría que se está viendo debe quedar en modo solo lectura.
 *
 * Bug real (producción, empresa sin ninguna asesoría activa — la única/última ya estaba
 * FINALIZADA): antes, cuando `asesoriasActivas` llegaba vacío, el efecto que llama a esta lógica
 * cortaba temprano sin llamar a esta función, dejando `enVerificacion` en su valor inicial
 * `false` (editable) para siempre — la matriz de una asesoría finalizada quedaba editable. Un
 * array vacío NO es un caso especial: simplemente no encuentra `asesoriaId` entre las activas,
 * y cae correctamente al mismo resultado de solo lectura que una asesoría histórica cualquiera.
 */
export function calcularEstadoEdicion(
  asesoriaId: number | undefined,
  asesoriasActivas: Array<{ asesoriaId: number; estado: string }>
): {
  enVerificacion: boolean;
  requiereCorrecciones: boolean;
  filtroRevision: FiltroRevision;
  /**
   * Motivo del solo-lectura para mostrar un mensaje preciso: 'EN_REVISION' cuando lo confirmamos
   * porque la asesoría aparece en la lista de activas con ese estado; 'DESCONOCIDO' cuando no
   * aparece entre las activas y no sabemos si es porque está FINALIZADA, porque es de otro
   * asesor (reasignación) o porque la lista aún no cargó — en ese caso NO hay que afirmar "en
   * revisión" sin evidencia (bug real: asesoría #110 estaba FINALIZADA y el mensaje decía "en
   * revisión" igual, porque el texto asumía que ese era el único motivo posible).
   */
  motivo: 'EN_REVISION' | 'DESCONOCIDO' | null;
} {
  const current = asesoriaId !== undefined ? asesoriasActivas.find((a) => a.asesoriaId === asesoriaId) : undefined;

  if (current) {
    const editable = isAsesoriaEditable(current.estado as AsesoriaEstado);
    const requiereCorrecciones = current.estado === 'REQUIERE_CORRECCIONES';
    return {
      enVerificacion: !editable,
      requiereCorrecciones,
      filtroRevision: requiereCorrecciones ? 'RECHAZOS' : 'TODOS',
      motivo: !editable ? 'EN_REVISION' : null,
    };
  }

  // No encontrada entre las activas (histórica, finalizada, o la lista aún no cargó): solo
  // lectura, pero no asumas por qué.
  return { enVerificacion: true, requiereCorrecciones: false, filtroRevision: 'TODOS', motivo: 'DESCONOCIDO' };
}

export function AsesoriasActivasNav({
  asesorias,
  currentAsesoriaId,
  nit,
}: {
  asesorias: Array<{ asesoriaId: number; estado: string; objetivo: string; orden: number | null }>;
  currentAsesoriaId: number;
  nit: string;
}) {
  const navigate = useNavigate();
  return (
    <div
      className="flex items-center gap-2 bg-white rounded-xl px-4 py-2.5 mb-4 shrink-0 overflow-x-auto"
      style={{ border: '1px solid var(--admin-border)' }}
    >
      <span className="text-[11px] font-medium shrink-0" style={{ color: 'var(--admin-text3)' }}>
        Asesorías activas
      </span>
      <div className="w-px h-3 bg-gray-200 shrink-0" />
      {asesorias.map((a) => {
        const isCurrent = a.asesoriaId === currentAsesoriaId;
        const color = a.estado === 'EN_CURSO' ? 'var(--admin-green)' : 'var(--admin-amber)';
        return (
          <button
            key={a.asesoriaId}
            onClick={() => navigate(`/empresa/${nit}/asesoria/${a.asesoriaId}/evaluacion`)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] shrink-0 transition-colors"
            style={{
              border: `1px solid ${isCurrent ? color : 'var(--admin-border)'}`,
              background: isCurrent ? `color-mix(in srgb, ${color} 10%, white)` : 'transparent',
              color: isCurrent ? color : 'var(--admin-text2)',
              fontWeight: isCurrent ? 600 : 400,
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
            {a.orden === 1 ? 'Diagnóstico' : a.orden != null ? `#${a.orden - 1}` : `#${a.asesoriaId}`}
            <span className="max-w-30 truncate">{a.objetivo.length > 30 ? a.objetivo.slice(0, 30) + '…' : a.objetivo}</span>
          </button>
        );
      })}
    </div>
  );
}

// Componente de estado de guardado
function EstadoGuardado({
  guardando,
  cambiosPendientes,
  ultimoGuardado,
}: {
  guardando: boolean;
  cambiosPendientes: number;
  ultimoGuardado: Date | null;
}) {
  if (guardando) {
    return (
      <div className="flex items-center gap-2" style={{ color: 'var(--admin-blue)' }}>
        <div
          className="w-3.5 h-3.5 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: 'var(--admin-blue)', borderTopColor: 'transparent' }}
        />
        <span className="text-[11px]">Guardando...</span>
      </div>
    );
  }

  if (cambiosPendientes > 0) {
    return (
      <div className="flex items-center gap-2" style={{ color: 'var(--admin-amber)' }}>
        <div className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ backgroundColor: 'var(--admin-amber)' }} />
        <span className="text-[11px]">{cambiosPendientes} cambios sin guardar</span>
      </div>
    );
  }

  if (ultimoGuardado) {
    return (
      <div className="flex items-center gap-2" style={{ color: 'var(--admin-green)' }}>
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
        <span className="text-[11px]">Guardado {formatDistanceToNow(ultimoGuardado, { addSuffix: true, locale: es })}</span>
      </div>
    );
  }

  return null;
}

export function EvaluacionPage() {
  const { nit, asesoriaId: asesoriaIdParam } = useParams<{ nit: string; asesoriaId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const asesoriaId = asesoriaIdParam ? Number(asesoriaIdParam) : undefined;
  const asesoriaLoading = false;
  const [enVerificacion, setEnVerificacion] = useState(false);
  const [motivoSoloLectura, setMotivoSoloLectura] = useState<'EN_REVISION' | 'DESCONOCIDO' | null>(null);
  const [requiereCorrecciones, setRequiereCorrecciones] = useState(false);

  // Leer filtros desde query params
  const requisitoIdFromQuery = searchParams.get('requisitoId') || '';

  // Cargar datos del API SOLO si el NIT es válido
  const debeCargar = !!nit && !!user && !asesoriaLoading;
  const {
    requisitos,
    ntcs,
    evidencias,
    ternas,
    requisitosSinTerna,
    evaluacionesTernas,
    evaluacionesTernasAnterior,
    comentariosAnteriores,
    historialAnterior,
    combinacion,
    isLoading,
    error,
    refetch,
  } = useRequisitosEvaluacion(nit || '', asesoriaId, undefined, debeCargar, undefined);

  const [filtroRequisitoId, setFiltroRequisitoId] = useState(requisitoIdFromQuery);
  const [asesoriasActivas, setAsesoriasActivas] = useState<
    Array<{ asesoriaId: number; estado: string; objetivo: string; orden: number | null }>
  >([]);
  const [guardando, setGuardando] = useState(false);
  const [ultimoGuardado, setUltimoGuardado] = useState<Date | null>(null);
  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);

  // Zustand store — per-requisito
  const cambios = useEvaluacionStore((s) => s.cambios);
  const revertidos = useEvaluacionStore((s) => s.revertidos);
  const initOriginales = useEvaluacionStore((s) => s.initOriginales);
  const limpiarCambiosEspecificos = useEvaluacionStore((s) => s.limpiarCambiosEspecificos);
  const limpiarRevertidos = useEvaluacionStore((s) => s.limpiarRevertidos);
  const resetStore = useEvaluacionStore((s) => s.resetStore);
  const inicializado = useEvaluacionStore((s) => s.inicializado);

  // Zustand store — per-terna
  const cambiosTerna = useEvaluacionTernaStore((s) => s.cambios);
  const initTernaOriginales = useEvaluacionTernaStore((s) => s.initOriginales);
  const initTernaAnteriores = useEvaluacionTernaStore((s) => s.initAnteriores);
  const limpiarCambiosTernasEspecificos = useEvaluacionTernaStore((s) => s.limpiarCambiosEspecificos);
  const resetTernaStore = useEvaluacionTernaStore((s) => s.resetStore);
  const ternaInicializado = useEvaluacionTernaStore((s) => s.inicializado);

  // Ref para el auto-guardado con debounce
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resetear stores de forma síncrona cuando cambia el NIT o la asesoría para evitar fugas de estado
  const prevIdRef = useRef<{ nit?: string; asesoriaId?: number }>({});
  if (prevIdRef.current.nit !== nit || prevIdRef.current.asesoriaId !== asesoriaId) {
    prevIdRef.current = { nit, asesoriaId };
    console.log('Reseteo síncrono del store por cambio de nit/asesoriaId:', nit, asesoriaId);
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    resetStore();
    resetTernaStore();
  }

  // Cargar asesorías activas para navegación múltiple
  useEffect(() => {
    if (!nit || !user) return;
    dashboardService
      .getAsesoriasActivas(nit)
      .then((res) => {
        if (res.success) {
          setAsesoriasActivas(res.asesorias);
        }
      })
      .catch(() => {});
  }, [nit, user]);

  const [filtroRevision, setFiltroRevision] = useState<FiltroRevision>('TODOS');
  const [revisionHistorial, setRevisionHistorial] = useState<any[]>([]);

  // Cargar historial de revisiones
  useEffect(() => {
    if (!nit || !asesoriaId) return;
    dashboardService
      .getRevisionHistorial(nit, asesoriaId)
      .then((res) => {
        if (res.success) {
          setRevisionHistorial(res.historial);
        }
      })
      .catch((err) => console.error('Error al cargar historial de revisiones:', err));
  }, [nit, asesoriaId]);

  // Detectar estado EN_REVISION y REQUIERE_CORRECCIONES de forma reactiva e inmediata.
  useEffect(() => {
    if (!asesoriaId) return;

    const { enVerificacion, requiereCorrecciones, filtroRevision, motivo } = calcularEstadoEdicion(asesoriaId, asesoriasActivas);
    setEnVerificacion(enVerificacion);
    setMotivoSoloLectura(motivo);
    setRequiereCorrecciones(requiereCorrecciones);
    setFiltroRevision(filtroRevision);
  }, [asesoriaId, asesoriasActivas]);

  // Inicializar valores originales INMEDIATAMENTE cuando llegan los datos
  useEffect(() => {
    if (requisitos.length > 0 && asesoriaId && !isLoading) {
      const asesoriaIdStr = asesoriaId.toString();
      const storeAsesoriaId = useEvaluacionStore.getState().asesoriaId;

      // Solo inicializar si es una asesoría diferente o no está inicializado
      if (storeAsesoriaId !== asesoriaIdStr || !inicializado) {
        console.log('Inicializando store con', requisitos.length, 'requisitos para asesoría', asesoriaIdStr);
        initOriginales(asesoriaIdStr, requisitos);
      } else {
        console.log('Store ya inicializado para asesoría', asesoriaIdStr);
      }
    }
  }, [requisitos, asesoriaId, inicializado, initOriginales, isLoading]);

  // Inicializar store de ternas cuando llegan los datos
  useEffect(() => {
    if (ternas.length > 0 && asesoriaId && !isLoading) {
      const asesoriaIdStr = asesoriaId.toString();
      const storeTernaAsesoriaId = useEvaluacionTernaStore.getState().asesoriaId;
      if (storeTernaAsesoriaId !== asesoriaIdStr || !ternaInicializado) {
        console.log('Inicializando terna store con', ternas.length, 'ternas para asesoría', asesoriaIdStr);
        initTernaOriginales(asesoriaIdStr, evaluacionesTernas, ternas);
      }
    }
  }, [ternas, evaluacionesTernas, asesoriaId, ternaInicializado, initTernaOriginales, isLoading]);

  // Inicializar baseline anterior de ternas (para diff antes/después)
  useEffect(() => {
    if (isLoading) return;
    const anteriorMap: Record<string, CambioTerna> = {};
    for (const et of evaluacionesTernasAnterior) {
      const key = et.grupo ? `grupo:${et.grupo}` : `terna:${et.ternaId}`;
      anteriorMap[key] = {
        observaciones: et.observaciones ?? '',
        planMejora: et.planMejora ?? '',
        fechaImplementacion: et.fechaImplementacion ?? '',
      };
    }
    initTernaAnteriores(anteriorMap);
  }, [evaluacionesTernasAnterior, isLoading, initTernaAnteriores]);

  // Agrupar en grupos (cliques máximos con restricción NTC)
  const grupos = useMemo(
    () => buildGruposEvaluacionDesdeTernas(ternas, requisitosSinTerna, requisitos, ntcs, combinacion?.ntcs),
    [ternas, requisitosSinTerna, requisitos, ntcs, combinacion]
  );

  // Identificar qué requisitos y ternas están rechazados o comentados
  const { requisitosRechazados, requisitosComentados, ternasRechazadas, ternasComentadas, tieneRechazos, tieneComentarios } =
    useMemo(() => {
      const reqRechazados = new Set<string>();
      const reqComentados = new Set<string>();
      const tRechazadas = new Set<string>();
      const tComentadas = new Set<string>();
      let hasRechazos = false;
      let hasComentarios = false;

      const porElemento = agruparHistorialPorElemento(revisionHistorial as RevisionEntry[]);
      for (const [, entries] of porElemento.entries()) {
        if (estaResuelto(entries)) continue;
        const ultimo = entries[entries.length - 1];
        if (!ultimo) continue;
        const isRechazado = ultimo.estado === 'RECHAZADO';
        const isComentado = ultimo.estado === 'COMENTADO';

        if (isRechazado) hasRechazos = true;
        if (isComentado) hasComentarios = true;

        if (ultimo.elementoTipo === 'REQUISITO') {
          const req = requisitos.find((r) => r.evaluacionRequisito?.id === Number(ultimo.elementoId) || r.id === ultimo.elementoId);
          if (req) {
            if (isRechazado) reqRechazados.add(req.id);
            if (isComentado) reqComentados.add(req.id);
          }
        } else if (['OBSERVACIONES', 'PLANMEJORA', 'FECHAIMPLEMENTACION'].includes(ultimo.elementoTipo)) {
          const evalTerna = evaluacionesTernas.find((et) => et.id === Number(ultimo.elementoId));
          if (evalTerna) {
            const evalKey = evalTerna.grupo ? `grupo:${evalTerna.grupo}` : `terna:${evalTerna.ternaId}`;
            if (isRechazado) tRechazadas.add(evalKey);
            if (isComentado) tComentadas.add(evalKey);
          }
        } else if (ultimo.elementoTipo === 'EVIDENCIA') {
          const evId = Number(ultimo.elementoId);
          evaluacionesTernas.forEach((et) => {
            if (et.evidenciaIds?.includes(evId)) {
              const evalKey = et.grupo ? `grupo:${et.grupo}` : `terna:${et.ternaId}`;
              if (isRechazado) tRechazadas.add(evalKey);
              if (isComentado) tComentadas.add(evalKey);
            }
          });
        }
      }

      // Salvaguarda: si los requisitos ya traen estadoRevision === 'RECHAZADO', los agregamos
      requisitos.forEach((req) => {
        if (req.evaluacionRequisito?.estadoRevision === 'RECHAZADO') {
          reqRechazados.add(req.id);
          hasRechazos = true;
        }
      });

      return {
        requisitosRechazados: reqRechazados,
        requisitosComentados: reqComentados,
        ternasRechazadas: tRechazadas,
        ternasComentadas: tComentadas,
        tieneRechazos: hasRechazos,
        tieneComentarios: hasComentarios,
      };
    }, [revisionHistorial, requisitos, evaluacionesTernas]);

  // Rechazos por evalKey (para mostrar el último rechazo + histórico inline en cada campo de terna).
  const rechazosPorEvalKey = useMemo(() => {
    const porElemento = agruparHistorialPorElemento(revisionHistorial as RevisionEntry[]);
    const mapa = new Map<string, RechazosTernaCampos>();
    for (const et of evaluacionesTernas) {
      const evalKey = et.grupo ? `grupo:${et.grupo}` : `terna:${et.ternaId}`;
      const cur = mapa.get(evalKey) ?? {};
      const obs = porElemento.get(`OBSERVACIONES:${et.id}`);
      const plan = porElemento.get(`PLANMEJORA:${et.id}`);
      const fecha = porElemento.get(`FECHAIMPLEMENTACION:${et.id}`);
      if (obs) cur.observaciones = [...(cur.observaciones ?? []), ...obs];
      if (plan) cur.planMejora = [...(cur.planMejora ?? []), ...plan];
      if (fecha) cur.fechaImplementacion = [...(cur.fechaImplementacion ?? []), ...fecha];
      mapa.set(evalKey, cur);
    }
    return mapa;
  }, [revisionHistorial, evaluacionesTernas]);

  // Ids de evidencias con rechazo vigente (para limitar la edición de checks en la matriz).
  const evidenciasRechazadasVigentes = useMemo(() => {
    const s = new Set<number>();
    const porElemento = agruparHistorialPorElemento(revisionHistorial as RevisionEntry[]);
    for (const [key, entries] of porElemento) {
      if (key.startsWith('EVIDENCIA:') && entries[entries.length - 1]?.estado === 'RECHAZADO') {
        const id = Number(key.slice('EVIDENCIA:'.length));
        if (!Number.isNaN(id)) s.add(id);
      }
    }
    return s;
  }, [revisionHistorial]);

  // Filtrar grupos si hay filtro por requisitoId o filtro por revisión
  const gruposFiltrados = useMemo<GrupoEvaluacion[]>(() => {
    return filterGruposRevision({
      grupos,
      filtroRequisitoId,
      filtroRevision,
      requisitosRechazados,
      requisitosComentados,
      ternasRechazadas,
      ternasComentadas,
    });
  }, [grupos, filtroRequisitoId, filtroRevision, requisitosRechazados, requisitosComentados, ternasRechazadas, ternasComentadas]);

  const rondaActual = useMemo(() => {
    if (revisionHistorial.length === 0) return 1;
    return Math.max(...revisionHistorial.map((h) => h.ronda));
  }, [revisionHistorial]);

  const keysRechazadasRondaActual = useMemo(() => {
    const s = new Set<string>();
    const porElemento = agruparHistorialPorElemento(revisionHistorial as RevisionEntry[]);
    for (const [key, entries] of porElemento.entries()) {
      if (tieneRechazoActivo(entries)) {
        s.add(key);
      }
    }
    return s;
  }, [revisionHistorial]);

  const tienePosterior = useMemo(() => {
    if (!asesoriaId || asesoriasActivas.length === 0) return false;
    return asesoriasActivas.some((a) => a.asesoriaId > asesoriaId);
  }, [asesoriaId, asesoriasActivas]);

  const cambiosPendientes = Object.keys(cambios).length + Object.keys(cambiosTerna).length + Object.keys(revertidos).length;

  // Función centralizada para guardar en el servidor
  const guardarEnServidor = useCallback(
    async (
      cambiosReqAGuardar: Record<string, CambioRequisito>,
      cambiosTernaAGuardar: Record<string, CambioTerna>,
      esGuardadoManual = false,
      revertidosAEliminar: Record<string, string> = {}
    ): Promise<boolean> => {
      if (!nit || !asesoriaId) {
        console.warn('No se puede guardar sin nit o asesoriaId');
        return false;
      }

      const hasReqChanges = Object.keys(cambiosReqAGuardar).length > 0;
      const hasTernaChanges = Object.keys(cambiosTernaAGuardar).length > 0;
      const hasRevertidos = Object.keys(revertidosAEliminar).length > 0;

      if (!hasReqChanges && !hasTernaChanges && !hasRevertidos) {
        console.log('No hay cambios para guardar');
        return true;
      }

      console.log(
        'Guardando',
        Object.keys(cambiosReqAGuardar).length,
        'cambios de requisitos,',
        Object.keys(cambiosTernaAGuardar).length,
        'cambios de ternas...'
      );
      setGuardando(true);

      try {
        const promises: Promise<unknown>[] = [];

        if (hasReqChanges || hasRevertidos) {
          promises.push(
            requisitosService.batchUpdate({
              nit,
              asesoriaId,
              cambios: cambiosReqAGuardar,
              ...(hasRevertidos ? { eliminar: Object.keys(revertidosAEliminar) } : {}),
            })
          );
        }

        if (hasTernaChanges) {
          const evalKeyToTernaId = useEvaluacionTernaStore.getState().evalKeyToTernaId;
          const cambiosArr: CambioTernaEvaluacion[] = Object.entries(cambiosTernaAGuardar)
            .filter(([evalKey]) => evalKeyToTernaId[evalKey] != null)
            .map(([evalKey, cambio]) => ({
              ternaId: evalKeyToTernaId[evalKey],
              ...cambio,
            }));
          if (cambiosArr.length > 0) {
            promises.push(requisitosService.batchUpdateTernas(nit, cambiosArr, asesoriaId));
          }
        }

        await Promise.all(promises);

        // Marcar como CORREGIDO los elementos rechazados que se acaban de editar.
        const elementosCorregidos: Array<{ elementoTipo: string; elementoId: string }> = [];
        const reqIdsCambiados = Object.keys(cambiosReqAGuardar);
        const ternasCambiadas = Object.entries(cambiosTernaAGuardar);

        for (const reqId of reqIdsCambiados) {
          if (!requisitosRechazados.has(reqId)) continue;
          const evalId = (requisitos.find((r) => r.id === reqId)?.evaluacionRequisito as { id?: number } | undefined)?.id;
          if (evalId != null) elementosCorregidos.push({ elementoTipo: 'REQUISITO', elementoId: String(evalId) });
        }

        for (const [evalKey, cambio] of ternasCambiadas) {
          const et = evaluacionesTernas.find((e) => (e.grupo ? `grupo:${e.grupo}` : `terna:${e.ternaId}`) === evalKey);
          if (!et) continue;
          const etId = String(et.id);
          const campos = rechazosPorEvalKey.get(evalKey);
          if (cambio.observaciones !== undefined && estaRechazadoVigente(campos?.observaciones ?? []))
            elementosCorregidos.push({ elementoTipo: 'OBSERVACIONES', elementoId: etId });
          if (cambio.planMejora !== undefined && estaRechazadoVigente(campos?.planMejora ?? []))
            elementosCorregidos.push({ elementoTipo: 'PLANMEJORA', elementoId: etId });
          if (cambio.fechaImplementacion !== undefined && estaRechazadoVigente(campos?.fechaImplementacion ?? []))
            elementosCorregidos.push({ elementoTipo: 'FECHAIMPLEMENTACION', elementoId: etId });
        }

        if (elementosCorregidos.length > 0 && nit && asesoriaId) {
          try {
            await asesoriasService.marcarCorregido(nit, asesoriaId, elementosCorregidos);
            const res = await dashboardService.getRevisionHistorial(nit, asesoriaId);
            if (res.success) setRevisionHistorial(res.historial);
          } catch (err) {
            console.error('Error marcando elementos corregidos:', err);
          }
        }

        // Si la asesoría cambió en el store durante la petición asíncrona, evitamos pisar el estado nuevo
        const storeId = useEvaluacionStore.getState().asesoriaId;
        const storeTernaId = useEvaluacionTernaStore.getState().asesoriaId;
        if (storeId !== asesoriaId.toString() || storeTernaId !== asesoriaId.toString()) {
          console.log('Asesoría cambió durante el guardado asíncrono. Ignorando actualización de store local.');
          return true;
        }

        console.log('✓ Guardado exitoso');

        // Sincronizar valores originales — requisitos
        if (hasReqChanges || hasRevertidos) {
          if (hasReqChanges) {
            evaluacionStore.setState((state) => {
              const nuevosOriginales = { ...state.valoresOriginales };
              for (const [reqId, cambio] of Object.entries(cambiosReqAGuardar)) {
                nuevosOriginales[reqId] = { ...nuevosOriginales[reqId], ...cambio };
              }
              return { valoresOriginales: nuevosOriginales };
            });
            limpiarCambiosEspecificos(Object.keys(cambiosReqAGuardar));
          }
          if (hasRevertidos) {
            limpiarRevertidos(Object.keys(revertidosAEliminar));
          }
        }

        // Sincronizar valores originales — ternas
        if (hasTernaChanges) {
          evaluacionTernaStore.setState((state) => {
            const nuevosOriginales = { ...state.valoresOriginales };
            for (const [evalKey, cambio] of Object.entries(cambiosTernaAGuardar)) {
              nuevosOriginales[evalKey] = { ...nuevosOriginales[evalKey], ...cambio };
            }
            return { valoresOriginales: nuevosOriginales };
          });
          limpiarCambiosTernasEspecificos(Object.keys(cambiosTernaAGuardar));
        }

        // Solo hacer refetch en guardado manual para evitar pérdida de scroll
        if (esGuardadoManual && typeof refetch === 'function') {
          const scrollEl = document.getElementById('matriz-scroll-container');
          const savedScrollTop = scrollEl?.scrollTop ?? 0;
          const savedScrollLeft = scrollEl?.scrollLeft ?? 0;
          await refetch();
          requestAnimationFrame(() => {
            const el = document.getElementById('matriz-scroll-container');
            if (el) {
              el.scrollTop = savedScrollTop;
              el.scrollLeft = savedScrollLeft;
            }
          });
        }

        return true;
      } catch (error) {
        // Detectar 409 — asesoría en verificación
        if (error instanceof Error && error.message.includes('409')) {
          setEnVerificacion(true);
        }
        console.error('Error al guardar:', error);
        return false;
      } finally {
        setGuardando(false);
      }
    },
    [
      nit,
      asesoriaId,
      refetch,
      limpiarCambiosEspecificos,
      limpiarCambiosTernasEspecificos,
      limpiarRevertidos,
      requisitosRechazados,
      requisitos,
      evaluacionesTernas,
      rechazosPorEvalKey,
    ]
  );

  useEffect(() => {
    // Si está en verificación, no hacer auto-guardado
    if (enVerificacion) return;
    // Si no hay cambios, no hacer nada
    const reqCount = Object.keys(cambios).length;
    const ternaCount = Object.keys(cambiosTerna).length;
    const revertidosCount = Object.keys(revertidos).length;
    if (reqCount === 0 && ternaCount === 0 && revertidosCount === 0) {
      return;
    }

    // Clonar cambios en el momento de programar
    const snapshotReq = { ...cambios };
    const snapshotTerna = { ...cambiosTerna };
    const snapshotRevertidos = { ...revertidos };

    console.log('Auto-guardado programado en', AUTO_SAVE_DELAY / 1000, 'segundos...');

    // Programar guardado después del delay
    autoSaveTimeoutRef.current = setTimeout(async () => {
      console.log('Ejecutando auto-guardado...');
      const exito = await guardarEnServidor(snapshotReq, snapshotTerna, false, snapshotRevertidos);
      if (exito) {
        setUltimoGuardado(new Date());
      }
    }, AUTO_SAVE_DELAY);

    // Cleanup
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
    };
  }, [cambios, cambiosTerna, revertidos, guardarEnServidor, enVerificacion]);

  // Guardar antes de salir de la página
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (Object.keys(cambios).length > 0 || Object.keys(cambiosTerna).length > 0 || Object.keys(revertidos).length > 0) {
        if (!guardando) {
          e.preventDefault();
          e.returnValue = ''; // Chrome requiere esto
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [cambios, guardando]);

  // Función para limpiar el filtro de requisitoId y ver todos
  const verTodosLosRequisitos = () => {
    setFiltroRequisitoId('');
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('requisitoId');
    navigate(`?${newSearchParams.toString()}`, { replace: true });
  };

  if (error) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--admin-bg)' }}>
        <div className="max-w-full px-4 py-4">
          <div className="admin-card p-12 text-center">
            <p className="text-[13px]" style={{ color: 'var(--admin-text3)' }}>
              Error al cargar la evaluación: {error}
            </p>
            <button onClick={() => navigate('/')} className="admin-btn mt-4">
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!nit) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--admin-bg)' }}>
        <div className="max-w-full px-4 py-4">
          <div className="admin-card p-12 text-center">
            <p className="text-[13px]" style={{ color: 'var(--admin-text3)' }}>
              Empresa no encontrada.
            </p>
            <button onClick={() => navigate('/')} className="admin-btn mt-4">
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Guardado manual - guarda y limpia el estado
  const handleGuardar = async () => {
    // Cancelar auto-save si está pendiente
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }

    const exito = await guardarEnServidor(cambios, cambiosTerna, true, revertidos);
    if (exito) {
      setUltimoGuardado(new Date());
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--admin-bg)' }}>
      <div className="max-w-full px-4 py-4 flex flex-col h-[calc(100vh-56px)]">
        {/* Back button */}
        <button
          onClick={() => navigate(`/empresa/${nit}/asesoria/${asesoriaIdParam}`)}
          className="flex items-center gap-1.5 text-[12px] mb-4 transition-colors"
          style={{ color: 'var(--admin-text3)' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--admin-text)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--admin-text3)')}
        >
          ← Volver a asesoría
        </button>

        {/* Header bar */}
        <div
          className="flex items-center justify-between bg-white rounded-xl px-4 py-3 mb-4 shrink-0"
          style={{ border: '1px solid var(--admin-border)' }}
        >
          {/* Left: title + asesoría id + filter select */}
          <div className="flex items-center gap-4">
            <span className="text-[13px] font-semibold" style={{ color: 'var(--admin-text)' }}>
              Evaluación de Requisitos
            </span>
            <RevisionFilterSelect
              tieneRechazos={tieneRechazos}
              tieneComentarios={tieneComentarios}
              value={filtroRevision}
              onChange={setFiltroRevision}
            />
          </div>

          {/* Right: save state + button */}
          <div className="flex items-center gap-3">
            {enVerificacion && (
              <span
                className="admin-chip text-[11px] font-medium"
                style={{ color: 'var(--admin-amber)', backgroundColor: 'var(--admin-amber-bg)' }}
              >
                {motivoSoloLectura === 'EN_REVISION' ? 'Solo lectura — en revisión' : 'Solo lectura'}
              </span>
            )}
            {asesoriaId && !enVerificacion && (
              <EstadoGuardado guardando={guardando} cambiosPendientes={cambiosPendientes} ultimoGuardado={ultimoGuardado} />
            )}
            {asesoriaId && !enVerificacion && (
              <button className="admin-btn" onClick={handleGuardar} disabled={cambiosPendientes === 0 || guardando}>
                {guardando ? 'Guardando...' : 'Guardar ahora'}
              </button>
            )}
          </div>
        </div>

        {/* Asesorías activas nav */}
        {asesoriasActivas.length > 1 && asesoriaId && (
          <AsesoriasActivasNav asesorias={asesoriasActivas} currentAsesoriaId={asesoriaId} nit={nit} />
        )}

        {/* Banner EN_REVISION */}
        {enVerificacion && (
          <div
            className="rounded-xl px-4 py-3 flex flex-col gap-1 mb-4 shrink-0"
            style={{ backgroundColor: 'var(--admin-amber-bg)', border: '1px solid #f0dba0' }}
          >
            <span className="text-[12px] font-medium" style={{ color: 'var(--admin-amber)' }}>
              {motivoSoloLectura === 'EN_REVISION'
                ? 'Asesoría en revisión — modo solo lectura'
                : 'Esta asesoría no está disponible para edición en este momento — modo solo lectura'}
            </span>
            {tienePosterior && (
              <span className="text-[11px] opacity-80" style={{ color: 'var(--admin-amber)' }}>
                Nota: Existe una asesoría posterior en curso. Las modificaciones de la nueva asesoría se conservarán en su propio borrador y
                se consolidarán cuando esta se finalice.
              </span>
            )}
          </div>
        )}

        {requiereCorrecciones && (
          <div
            className="rounded-xl px-4 py-3 flex items-center gap-2 mb-4 shrink-0"
            style={{ backgroundColor: '#fdf0ee', border: '1px solid #f5c6c0' }}
          >
            <AlertCircle className="w-3.5 h-3.5 shrink-0" style={{ color: '#c0392b' }} />
            <span className="text-[12px] font-medium" style={{ color: '#c0392b' }}>
              Requiere correcciones — cada elemento rechazado muestra abajo el comentario del administrador y su histórico. Corrígelos antes
              de volver a enviar.
            </span>
          </div>
        )}

        {/* Banner de filtro por requisito específico */}
        {filtroRequisitoId && (
          <div
            className="rounded-xl px-4 py-3 flex items-center justify-between mb-4 shrink-0"
            style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}
          >
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" style={{ color: 'var(--admin-blue)' }} />
              <span className="text-[12px]" style={{ color: 'var(--admin-blue)' }}>
                Mostrando únicamente el requisito <strong>{filtroRequisitoId}</strong>
              </span>
            </div>
            <button className="admin-btn text-[11px]" onClick={verTodosLosRequisitos}>
              Ver todos
            </button>
          </div>
        )}

        {/* Matriz de Evaluación */}
        <div className="flex-1 min-h-0 min-w-0 flex flex-col relative">
          {isLoading || !inicializado || (ternas.length > 0 && !ternaInicializado) ? (
            <div className="flex-1 flex flex-col items-center justify-center py-24 bg-white rounded-xl border border-(--admin-border)">
              <Spinner size="md" className="mb-3" />
              <span className="text-[13px]" style={{ color: 'var(--admin-text3)' }}>
                Cargando evaluación...
              </span>
            </div>
          ) : (
            <MatrizEvaluacion
              grupos={gruposFiltrados}
              evidencias={evidencias}
              ntcs={ntcs}
              columnasNtc={combinacion?.ntcs}
              className="flex-1 min-h-0 min-w-0"
              disabled={enVerificacion}
              rechazosPorEvalKey={rechazosPorEvalKey}
              soloEditablesRechazados={requiereCorrecciones}
              evidenciasRechazadas={evidenciasRechazadasVigentes}
              evaluacionesTernas={evaluacionesTernas}
              revisionHistorial={revisionHistorial}
              keysRechazadasRondaActual={keysRechazadasRondaActual}
              rondaActual={rondaActual}
              comentariosAnteriores={comentariosAnteriores}
              historialAnterior={historialAnterior}
              asesoriaLabelFn={(id) => {
                const a = asesoriasActivas.find((x) => x.asesoriaId === id);
                if (!a) return `#${id}`;
                if (a.orden === 1) return 'Diagnóstico';
                return a.orden != null ? `#${a.orden - 1}` : `#${id}`;
              }}
            />
          )}
        </div>

        {/* Botón flotante de guardado — solo cuando hay cambios o se acaba de guardar */}
        {asesoriaId && !enVerificacion && (cambiosPendientes > 0 || guardando || ultimoGuardado) && (
          <div
            className="fixed bottom-6 right-6 flex items-center gap-3 bg-white rounded-full shadow-lg px-6 py-3"
            style={{ border: '1px solid var(--admin-border)' }}
          >
            <EstadoGuardado guardando={guardando} cambiosPendientes={cambiosPendientes} ultimoGuardado={ultimoGuardado} />
            {cambiosPendientes > 0 && (
              <button className="admin-btn" onClick={handleGuardar} disabled={guardando}>
                Guardar ahora
              </button>
            )}
          </div>
        )}

        {/* Burbuja de acciones rápidas */}
        {!enVerificacion && (
          <QuickActionsSpeedDial
            actions={[
              {
                icon: <FileUp className="w-5 h-5" />,
                label: 'Subir Evidencia',
                onClick: () => setIsEvidenceModalOpen(true),
              },
            ]}
          />
        )}

        {/* Modal de nueva evidencia */}
        <NuevaEvidenciaModal
          isOpen={isEvidenceModalOpen}
          onClose={() => setIsEvidenceModalOpen(false)}
          nit={nit!}
          asesoriaId={asesoriaId!}
          onSuccess={refetch}
          disabled={enVerificacion}
          disabledMessage="Esta asesoria esta en revisión y no puede modificarse."
        />
      </div>
    </div>
  );
}
